import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import JuntoApiService, { User } from '../services/api';
import { PageWrapper } from '../components/PageWrapper/PageWrapper';
import { ResponsiveTable } from '../components/ResponsiveTable/ResponsiveTable';
import EntityActionButtons from '../components/EntityActionButtons/EntityActionButtons';

const UsersPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
  });

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await JuntoApiService.getUsers(includeInactive);
      
      // Ensure data is an array
      if (Array.isArray(data)) {
        setUsers(data);
        setError(null);
      } else {
        console.error('Expected array of users, got:', typeof data, data);
        setUsers([]);
        setError('Invalid data format received from server');
      }
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      date_of_birth: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (user: User) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      date_of_birth: user.date_of_birth || '',
      emergency_contact_name: user.emergency_contact_name || '',
      emergency_contact_phone: user.emergency_contact_phone || '',
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setDialogError(null);
  };

  const handleSave = async () => {
    try {
      setDialogError(null);
      
      const userData = {
        ...formData,
        id: editingUser?.id || `user-${Date.now()}`,
        created_at: editingUser?.created_at || new Date().toISOString(),
      };

      if (editingUser) {
        // Update existing user
        await JuntoApiService.updateUser(userData);
      } else {
        // Create new user
        await JuntoApiService.createUser(userData);
      }
      
      await loadUsers();
      closeDialog();
    } catch (err) {
      console.error('Failed to save user:', err);
      setDialogError('Failed to save user. Please check your input.');
    }
  };

  const handleSyncClerkUsers = async () => {
    try {
      setSyncing(true);
      setSyncMessage(null);
      setError(null);
      
      const result = await JuntoApiService.syncAllClerkUsers();
      setSyncMessage(`Sync completed: ${result.synced_count} synced, ${result.skipped_count} skipped, ${result.total_processed} total processed`);
      
      // Reload users to show updated data
      await loadUsers();
    } catch (err) {
      console.error('Failed to sync users:', err);
      setError('Failed to sync users from Clerk. Please try again.');
    } finally {
      setSyncing(false);
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading users...</Typography>
      </Box>
    );
  }

  return (
    <PageWrapper
      title="Users"
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                icon={<VisibilityOffIcon />}
                checkedIcon={<VisibilityIcon />}
                size={isMobile ? 'small' : 'medium'}
              />
            }
            label={isMobile ? '' : 'Show Inactive'}
            sx={{ mr: 0 }}
          />
          <Button
            variant="outlined"
            startIcon={!isMobile && <SyncIcon />}
            onClick={handleSyncClerkUsers}
            disabled={syncing}
            size={isMobile ? 'small' : 'medium'}
            sx={{ minWidth: isMobile ? 'auto' : '120px' }}
          >
            {syncing ? (
              <CircularProgress size={16} />
            ) : (
              isMobile ? <SyncIcon /> : 'Re-sync'
            )}
          </Button>
          <Button
            variant="contained"
            startIcon={!isMobile && <AddIcon />}
            onClick={openCreateDialog}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? <AddIcon /> : 'Add User'}
          </Button>
        </Stack>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button size="small" onClick={loadUsers} sx={{ ml: 2 }}>Retry</Button>
        </Alert>
      )}
      
      {syncMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSyncMessage(null)}>
          {syncMessage}
        </Alert>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <ResponsiveTable component={Paper}>
          <Table stickyHeader={!isMobile}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {!isMobile && <TableCell>Email</TableCell>}
                {!isTablet && <TableCell>Phone</TableCell>}
                {!isTablet && <TableCell>Emergency Contact</TableCell>}
                {!isMobile && <TableCell>Role</TableCell>}
                {!isMobile && <TableCell>Created</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow 
                  key={user.id} 
                  hover
                >
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {!isMobile && <PersonIcon color="primary" sx={{ mr: 1 }} />}
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {user.name}
                        </Typography>
                        {isMobile && (
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <EmailIcon color="action" sx={{ mr: 1, fontSize: '1rem' }} />
                        <Typography variant="body2">{user.email}</Typography>
                      </Box>
                    </TableCell>
                  )}
                  {!isTablet && (
                    <TableCell>
                      {user.phone ? (
                        <Box display="flex" alignItems="center">
                          <PhoneIcon color="action" sx={{ mr: 1, fontSize: '1rem' }} />
                          <Typography variant="body2">{user.phone}</Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">N/A</Typography>
                      )}
                    </TableCell>
                  )}
                  {!isTablet && (
                    <TableCell>
                      {user.emergency_contact_name ? (
                        <Box>
                          <Typography variant="body2">{user.emergency_contact_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.emergency_contact_phone}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">N/A</Typography>
                      )}
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Chip 
                        label={user.clerk_role || 'customer'}
                        color={user.clerk_role === 'super_admin' ? 'error' : 
                               user.clerk_role === 'admin' ? 'warning' :
                               user.clerk_role === 'instructor' ? 'info' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(user.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <EntityActionButtons
                      entity={user}
                      entityType="users"
                      entityDisplayName="User"
                      onEdit={openEditDialog}
                      onRefresh={loadUsers}
                      renderEntityInHistory={(entity) => (
                        <Box>
                          <Typography variant="body1">
                            <strong>Name:</strong> {entity.name}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Email:</strong> {entity.email}
                          </Typography>
                          {entity.phone && (
                            <Typography variant="body1">
                              <strong>Phone:</strong> {entity.phone}
                            </Typography>
                          )}
                          {entity.date_of_birth && (
                            <Typography variant="body1">
                              <strong>Date of Birth:</strong> {new Date(entity.date_of_birth).toLocaleDateString()}
                            </Typography>
                          )}
                          {entity.emergency_contact_name && (
                            <Typography variant="body1">
                              <strong>Emergency Contact:</strong> {entity.emergency_contact_name}
                              {entity.emergency_contact_phone && ` (${entity.emergency_contact_phone})`}
                            </Typography>
                          )}
                        </Box>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ResponsiveTable>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={closeDialog} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Emergency Contact Name"
                value={formData.emergency_contact_name}
                onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Emergency Contact Phone"
                value={formData.emergency_contact_phone}
                onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
              />
            </Grid>
            
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageWrapper>
  );
};

export default UsersPage;